import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Loader2, Receipt, CalendarClock, Users, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { payments } from '../api';

const money = (n, cur = 'ZAR') => (cur === 'ZAR' ? 'R ' : cur + ' ') + (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const daysUntil = (iso) => (iso ? Math.max(0, Math.ceil((new Date(iso) - Date.now()) / 86400000)) : null);

const STATUS_STYLES = {
  trial: 'bg-sky-50 text-sky-800 ring-sky-200',
  active: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
  expired: 'bg-red-50 text-red-700 ring-red-200',
};

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-lg border border-stone-200 p-4">
    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-stone-500 font-semibold"><Icon size={12}/> {label}</div>
    <div className="mt-1.5 text-lg font-semibold text-stone-900" data-testid={testid}>{value}</div>
  </div>
);

const HistoryTable = ({ rows }) => {
  if (!rows.length) return <div className="p-6 text-sm text-stone-500 text-center" data-testid="payment-history-empty">No payments yet.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="payment-history-table">
        <thead className="bg-stone-50 text-stone-500 text-xs uppercase tracking-wider">
          <tr><th className="text-left px-5 py-2.5">Date</th><th className="text-left px-5 py-2.5">Plan</th><th className="text-right px-5 py-2.5">Amount</th><th className="text-left px-5 py-2.5">Reference</th><th className="text-left px-5 py-2.5">Status</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const paid = r.payment_status === 'paid' || r.status === 'completed';
            return (
              <tr key={r.id || r.reference} className="border-t border-stone-100" data-testid={`payment-row-${r.reference}`}>
                <td className="px-5 py-3 text-stone-700">{fmtDate(r.paidAt || r.createdAt)}</td>
                <td className="px-5 py-3 capitalize text-stone-700">{r.plan}</td>
                <td className="px-5 py-3 text-right font-medium text-stone-900">{money(r.amount, r.currency)}</td>
                <td className="px-5 py-3 font-mono text-xs text-stone-600">{r.reference}</td>
                <td className="px-5 py-3">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ring-1 ${paid ? STATUS_STYLES.active : 'bg-amber-50 text-amber-800 ring-amber-200'}`}>{paid ? 'Paid' : (r.payment_status || r.status || 'pending')}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export const SubscriptionPanel = ({ user }) => {
  const nav = useNavigate();
  const [history, setHistory] = useState([]);
  const [seats, setSeats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([payments.history().catch(() => []), payments.plans().catch(() => ({}))])
      .then(([h, p]) => { setHistory(Array.isArray(h) ? h : []); setSeats(p?.userCount ?? null); })
      .finally(() => setLoading(false));
  }, []);

  const isTrial = user?.accessStatus === 'trial';
  const status = user?.accessStatus === 'locked' ? 'expired' : (isTrial ? 'trial' : 'active');
  const renewal = isTrial ? user?.trialEndsAt : user?.paidUntil;
  const daysLeft = isTrial ? (user?.daysLeftInTrial ?? daysUntil(renewal)) : daysUntil(renewal);
  const plan = user?.plan || 'monthly';
  const planLabel = isTrial && !user?.plan ? 'Free trial' : `${plan.charAt(0).toUpperCase()}${plan.slice(1)}`;

  return (
    <div className="space-y-6" data-testid="subscription-panel">
      <div className="rounded-xl bg-white border border-stone-200 overflow-hidden">
        <div className="p-5 border-b border-stone-100 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2"><CreditCard size={18} className="text-emerald-800"/><div className="font-semibold text-stone-900">Current subscription</div></div>
          <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold capitalize ring-1 ${STATUS_STYLES[status]}`} data-testid="subscription-status-badge">{status}</span>
        </div>
        <div className="p-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat icon={CreditCard} label="Plan" value={planLabel} testid="subscription-plan"/>
          <Stat icon={Users} label="Seats" value={seats ?? '—'} testid="subscription-seats"/>
          <Stat icon={CalendarClock} label={isTrial ? 'Trial ends' : 'Next renewal'} value={fmtDate(renewal)} testid="subscription-renewal-date"/>
          <Stat icon={RefreshCw} label="Days left" value={daysLeft ?? '—'} testid="subscription-days-left"/>
        </div>
        <div className="px-5 py-4 bg-stone-50 border-t border-stone-100 flex items-center justify-between gap-4 flex-wrap">
          <div className="text-sm text-stone-600">{status === 'expired' ? 'Your access is locked. Renew to restore it instantly.' : 'Renewing now extends your access from your current end date.'}</div>
          <Button onClick={() => nav(`/pay?plan=${plan}`)} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="subscription-renew-btn">
            <CreditCard size={16} className="mr-2"/> Renew now
          </Button>
        </div>
      </div>

      <div className="rounded-xl bg-white border border-stone-200 overflow-hidden">
        <div className="p-5 border-b border-stone-100 flex items-center gap-2"><Receipt size={18} className="text-emerald-800"/><div className="font-semibold text-stone-900">Payment history</div></div>
        {loading ? <div className="p-6 flex justify-center text-stone-500"><Loader2 size={18} className="animate-spin"/></div> : <HistoryTable rows={history}/>}
      </div>
    </div>
  );
};
